import { useTRPC, useTRPCClient } from '@/utils/trpc';
import type { MedicationListRecord } from '@liza/fhir-utils';
import type { PatientAdminApiRouterInputs } from '@liza/patient-admin-api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

/** The list options the record-list procedures accept, taken from the router so it cannot drift. */
type RecordListOptions = PatientAdminApiRouterInputs['patient']['medicationRequest']['list']['options'];

/**
 * Page size for the medication scans. The pages are fetched to exhaustion, so this trades request
 * count against payload size rather than capping the result.
 */
const PAGE_SIZE = 100;

/**
 * Every medication record for a patient under the given list options — both resource types, all
 * pages.
 *
 * The medication surfaces group by RxNorm, and a group is only correct if every member is present:
 * a partial scan does not merely show fewer rows, it can show a plan without the prescription that
 * superseded it, or a count that disagrees with the chart. So these pages are fetched to exhaustion
 * rather than capped — a chart accumulates a superseded record per e-prescribe cycle, and the worst
 * in production carries 215 against a single page of 100 (#9611).
 *
 * `isPending` stays true until both resource types have finished paging, so callers never group a
 * half-loaded chart and render a count that changes under the reader.
 */
export function useAllMedicationRecords(
  medplumPatientId: string,
  options: RecordListOptions,
  enabled: boolean,
): { records: MedicationListRecord[]; isPending: boolean } {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();

  const requestsQuery = useInfiniteQuery({
    queryKey: trpc.patient.medicationRequest.list.queryKey({ medplumPatientId, options }),
    queryFn: async ({ pageParam }) =>
      trpcClient.patient.medicationRequest.list.query({
        medplumPatientId,
        options,
        offset: pageParam,
        count: PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastOffset) => (lastPage.hasMore ? lastOffset + PAGE_SIZE : undefined),
    enabled,
  });

  const statementsQuery = useInfiniteQuery({
    queryKey: trpc.patient.medicationStatement.list.queryKey({ medplumPatientId, options }),
    queryFn: async ({ pageParam }) =>
      trpcClient.patient.medicationStatement.list.query({
        medplumPatientId,
        options,
        offset: pageParam,
        count: PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastOffset) => (lastPage.hasMore ? lastOffset + PAGE_SIZE : undefined),
    enabled,
  });

  // Drive both to exhaustion. Keyed on the page count as well as `hasNextPage`: the latter stays
  // `true` for every page but the last, so an effect watching only it never re-fires and the scan
  // stops after page two.
  const { hasNextPage: hasMoreRequests, fetchNextPage: fetchRequests } = requestsQuery;
  const requestPages = requestsQuery.data?.pages.length ?? 0;
  useEffect(() => {
    if (hasMoreRequests) void fetchRequests();
  }, [hasMoreRequests, requestPages, fetchRequests]);

  const { hasNextPage: hasMoreStatements, fetchNextPage: fetchStatements } = statementsQuery;
  const statementPages = statementsQuery.data?.pages.length ?? 0;
  useEffect(() => {
    if (hasMoreStatements) void fetchStatements();
  }, [hasMoreStatements, statementPages, fetchStatements]);

  const records = useMemo(() => {
    const requests = requestsQuery.data?.pages.flatMap((page) => page.medicationRequests) ?? [];
    const statements = statementsQuery.data?.pages.flatMap((page) => page.medicationStatements) ?? [];
    return [...requests, ...statements];
  }, [requestsQuery.data, statementsQuery.data]);

  // Still paging is still pending: a caller that grouped now would show a count it has to revise.
  // A disabled query never leaves `isPending`, so gate on `enabled` as the existing hooks do.
  const isPending =
    enabled &&
    (requestsQuery.isPending || statementsQuery.isPending || Boolean(hasMoreRequests) || Boolean(hasMoreStatements));

  return { records, isPending };
}
